/**
 * Global Stock Search Component
 *
 * A search bar in the navbar with autocomplete that navigates to Stock Research page.
 *
 * Performance Optimization (Jan 2026):
 * - Uses preloaded symbol list for INSTANT (<10ms) local search
 * - No debounce needed - search happens on every keystroke
 * - Falls back to API search if local list not loaded
 *
 * Created Dec 2024 to replace GlobalAssetSearch which depended on agent stores.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
  ClickAwayListener,
  Chip,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { searchSymbols, SearchResult } from '../services/symbolSearchService';
import { useSearchHistoryStore } from '../stores/searchHistoryStore';
import { useAuth } from '../contexts/AuthContext';
import { useSymbolListStore } from '../stores/symbolListStore';
import analyticsService from '../services/analytics';
import { useOnboardingChecklistSafe } from '../contexts/OnboardingChecklistContext';

const GlobalStockSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const theme = useTheme();

  // Preloaded symbol list for instant search
  const { isLoaded: symbolsLoaded, searchSymbols: localSearch, loadSymbols } = useSymbolListStore();

  // Search history tracking
  const { addSearch } = useSearchHistoryStore();
  const { user } = useAuth();

  // Onboarding task tracking (safe access - context may not be available)
  const onboardingContext = useOnboardingChecklistSafe();
  const completeFirstSearch = useCallback(() => {
    // Only complete if not already completed (prevent duplicate celebration popups)
    if (onboardingContext && !onboardingContext.tasks.firstSearch) {
      onboardingContext.completeTask('firstSearch');
    }
  }, [onboardingContext]);

  // Initialize symbol list on mount
  useEffect(() => {
    loadSymbols();
  }, [loadSymbols]);

  const trackSearch = useCallback((symbol: string, name: string) => {
    if (user?.uid && symbol) {
      addSearch(user.uid, { symbol, name: name || symbol, type: 'stock' });
    }
  }, [user?.uid, addSearch]);

  // Search effect - instant local search or debounced API fallback
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 1) {
      // If local symbols are loaded, show instant results then supplement with FMP API
      if (symbolsLoaded) {
        const localResults = localSearch(searchQuery);
        const formattedResults: SearchResult[] = localResults.map(r => ({
          symbol: r.symbol,
          name: r.name,
          exchange: r.exchange,
          exchangeDisplay: r.exchange,
          type: 'stock'
        }));
        setSearchResults(formattedResults.slice(0, 8));
        setIsSearching(false);
        setIsOpen(true);

        // Also query FMP API in background to catch ASX/international stocks
        searchTimeoutRef.current = setTimeout(async () => {
          try {
            const fullResults = await searchSymbols(searchQuery);
            if (fullResults.length > 0) {
              setSearchResults(fullResults.slice(0, 10));
            }
          } catch (error) {
            // Silently fail — local results already shown
          }
        }, 300);
      } else {
        // Fallback: API search with debounce
        setIsSearching(true);
        searchTimeoutRef.current = setTimeout(async () => {
          try {
            const results = await searchSymbols(searchQuery);
            setSearchResults(results.slice(0, 8));
            // Always open dropdown when typing
            setIsOpen(true);
          } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
          } finally {
            setIsSearching(false);
          }
        }, 300); // 300ms debounce for API
      }
    } else {
      setSearchResults([]);
      setIsSearching(false);
      setIsOpen(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, symbolsLoaded, localSearch]);

  // Handle selecting a result from autocomplete
  const handleSelectResult = (result: SearchResult) => {
    // Track search in history for authenticated users
    trackSearch(result.symbol, result.name);
    // Track analytics
    analyticsService.trackStockViewed(result.symbol, 'navbar_search');
    // Complete onboarding task
    completeFirstSearch();

    navigate(`/research/stock/${encodeURIComponent(result.symbol)}`);
    setSearchQuery('');
    setSearchResults([]);
    setIsOpen(false);
  };

  // Handle search submission (Enter key)
  const handleSearch = () => {
    const query = searchQuery.trim().toUpperCase();
    if (query) {
      // Track direct search (without selecting from dropdown)
      trackSearch(query, query);
      // Track analytics
      analyticsService.trackStockSearch(query, searchResults.length, 'navbar_search');
      analyticsService.trackStockViewed(query, 'navbar_search_direct');
      // Complete onboarding task
      completeFirstSearch();

      navigate(`/research/stock/${encodeURIComponent(query)}`);
      setSearchQuery('');
      setSearchResults([]);
      setIsOpen(false);
    }
  };

  // Handle key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      // If there's an exact match in results, use it
      const exactMatch = searchResults.find(
        r => r.symbol.toUpperCase() === searchQuery.trim().toUpperCase()
      );
      if (exactMatch) {
        handleSelectResult(exactMatch);
      } else {
        handleSearch();
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsOpen(false);
  };

  // Handle click away
  const handleClickAway = () => {
    setIsOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        <TextField
          size="small"
          placeholder="Search stocks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyPress}
          onFocus={() => searchResults.length > 0 && setIsOpen(true)}
          sx={{
            width: 200,
            '& .MuiOutlinedInput-root': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              borderRadius: 1,
              '& fieldset': {
                borderColor: 'transparent'
              },
              '&:hover fieldset': {
                borderColor: 'divider'
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 1
              }
            },
            '& .MuiInputBase-input': {
              py: 0.75,
              fontSize: '0.875rem'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isSearching ? (
                  <CircularProgress size={16} />
                ) : searchQuery ? (
                  <IconButton size="small" onClick={handleClear}>
                    <Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null}
              </InputAdornment>
            )
          }}
        />

        {/* Autocomplete Dropdown */}
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1301, width: 280 }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 0.5,
              maxHeight: 350,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <List dense disablePadding>
              {searchResults.map((result) => (
                <ListItemButton
                  key={result.symbol}
                  onClick={() => handleSelectResult(result)}
                  sx={{
                    py: 1,
                    px: 1.5,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: 'text.primary' }}
                        >
                          {result.symbol}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            px: 0.5,
                            borderRadius: 0.5,
                            fontSize: '0.65rem'
                          }}
                        >
                          {result.exchangeDisplay}
                        </Typography>
                        {result.isEtf && (
                          <Chip
                            label="ETF"
                            size="small"
                            color="primary"
                            sx={{
                              height: 16,
                              fontSize: '0.65rem',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {result.name}
                      </Typography>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalStockSearch;
